import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { useEffectOnce } from '@carvana/merch-react-hooks';
import { datadogRum } from '@datadog/browser-rum';
import { useContext, useEffect } from 'react';
import { useSelector } from 'react-redux';
import VehiclesAvailableSoonerContext from '~/context/vdp/VehiclesAvailableSoonerContext';
import { usePriceDrop } from '~/hooks/vdp';
import {
  getExperiments,
  getExperimentLoaded,
  getUserAvailability,
  getVehicleDetails,
  getSoftPullStatus,
  getUser,
  getAlertStatus
} from '~/selectors';
import createSelector from '~/store/createSelector';
import { BrowserCookies } from '~/utilities';
import {
  useSendDeliveryMiles,
  useSendDeliveryPickup,
  useSendTransitCharge,
  useSendZipCodeChanges
} from './events';

const getUserFinancing = createSelector(
  getUser,
  getSoftPullStatus,
  (user, softPullStatus) => ({
    isLoggedInAndFinancingLoaded: user.isLoggedIn && user.financing.isLoaded,
    softPullStatus
  })
);

/**
 * Since trackEvent is not available within redux actions,
 * we can send data related events here
 */
const useSendDataEvents = () => {
  const { trackEvent } = useContext(AnalyticsContext);
  const vehiclesAvailableSooner = useContext(VehiclesAvailableSoonerContext);
  const {
    action,
    error,
    lockType,
    isLoaded: availabilityLoaded
  } = useSelector(getUserAvailability);
  const {
    purchasePending,
    unavailable,
    stopSaleRecall,
    outOfMarket,
    isSold,
    notAvailableInCA
  } = useSelector(getAlertStatus);
  const areExperimentsLoaded = useSelector(getExperimentLoaded);
  const experiments = useSelector(getExperiments);
  const { isLoggedInAndFinancingLoaded, softPullStatus } =
    useSelector(getUserFinancing);
  const { inventoryType, stores, vehicleId } = useSelector(getVehicleDetails);
  const { sendPriceDropEvent } = usePriceDrop();

  useEffectOnce(
    () => {
      trackEvent('Availability data', { action, error, lockType });
    },
    [action, error, lockType, trackEvent],
    Boolean(availabilityLoaded)
  );

  useEffect(() => {
    if (!areExperimentsLoaded) return;
    const experimentsWithBucket = Object.keys(experiments).reduce(
      (accum, key) => {
        accum[key] = experiments[key].assignedBucket;

        return accum;
      },
      {}
    );
    datadogRum.setGlobalContextProperty('experiments', experimentsWithBucket);
    datadogRum.setGlobalContextProperty('inventoryType', inventoryType);
    datadogRum.setGlobalContextProperty('stores', stores);
    datadogRum.setGlobalContextProperty('Geolocation', {
      CVCurrentState: BrowserCookies.get('CVCurrentState'),
      CVCurrentZip: BrowserCookies.get('CVCurrentZip')
    });
  }, [areExperimentsLoaded, experiments, inventoryType, stores]);

  // We only care what the user's soft pull status is when they arrive at the page.
  useEffectOnce(
    () => {
      trackEvent('Soft Pull', { softPullStatus });
    },
    [softPullStatus, trackEvent],
    isLoggedInAndFinancingLoaded
  );

  useSendTransitCharge();

  useSendDeliveryMiles();

  useSendDeliveryPickup();

  useSendZipCodeChanges();

  useEffect(() => {
    if (!areExperimentsLoaded) return;
    sendPriceDropEvent();
  }, [areExperimentsLoaded, sendPriceDropEvent]);

  useEffect(() => {
    if (!vehiclesAvailableSooner.isLoaded || vehiclesAvailableSooner.hasError) {
      return;
    }
    trackEvent('Vehicles Available Sooner', {
      vehicles: vehiclesAvailableSooner.data
    });
  }, [
    trackEvent,
    vehiclesAvailableSooner.data,
    vehiclesAvailableSooner.hasError,
    vehiclesAvailableSooner.isLoaded
  ]);

  useEffect(() => {
    const alerts = [
      { type: purchasePending, description: 'vehicle-purchase-pending' },
      { type: unavailable, description: 'vehicle-unavailable' },
      { type: stopSaleRecall, description: 'vehicle-stop-sale-recall' },
      { type: outOfMarket, description: 'vehicle-out-of-market' },
      { type: isSold, description: 'vehicle-is-sold' },
      { type: notAvailableInCA, description: 'vehicle-not-available-in-ca' }
    ];

    alerts.forEach(alert => {
      if (alert.type)
        datadogRum.addAction(`vdp-alert-${alert.description}`, { vehicleId });
    });
  }, [
    isSold,
    notAvailableInCA,
    outOfMarket,
    purchasePending,
    stopSaleRecall,
    unavailable,
    vehicleId
  ]);
};

export default useSendDataEvents;
